﻿// @flow

const frameMinHeight = 580;
// SteepGraph Modification
// Developer    : Pratik Daud
// Date         : 06/04/2026
// Purpose      : Define default height for relationship iframe when not maximized.
// ==============================================
// Changes Start
const relationshipInitialHeight = 600;
//Changes End

export default class Scroller extends HyperHTMLElement {
	_mutationObserver: ?MutationObserver = null;
	_intersectionObserver: ?IntersectionObserver = null;
	_resizeObserver: ?ResizeObserver = null;
	_scrollerTop: number = 0;
	_currentStickedElement: ?HTMLElement = null;
	_store: WeakMap<HTMLElement, HTMLElement> = new WeakMap();
	// SteepGraph Modification
	// Developer    : Pratik Daud
	// Date         : 06/04/2026
	// Purpose      : Track accordion wrapper elements and global maximize state.
	// ==============================================
	// Changes Start
	_wrappers: Set<HTMLElement> = new Set();
	_isMaximized: boolean = false;
    //Changes End
	created() {
		this._mutationObserver = new MutationObserver((mutations) => {
			this._mutationObserverHandler(mutations);
		});
		this._mutationObserver.observe(this, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['active-pane-id']
		});
		this._intersectionObserver = new IntersectionObserver(
			(changes) => {
				this._intersectionObserverHandler(changes);
			},
			{
				root: this,
				threshold: [0, 0.999, 1]
			}
		);
		this.addEventListener('accordionToggle', this._accordionToggleHandler);
		// SteepGraph Modification
		// Developer    : Pratik Daud
		// Date         : 06/04/2026
		// Purpose      : Bind accordion maximize event to dynamically resize scroller content.
		// ==============================================
		// Changes Start
		// Store bound reference so we can remove it properly in disconnectedCallback
		this._boundResizeAccordion = this._resizeAccordion.bind(this);
		this.addEventListener('accordionMaximize', this._boundResizeAccordion);
		//Changes End
		this.addEventListener('scroll', this._scrollHandler);
		window.addEventListener('resize', this._resizeHandler);
	}

	connectedCallback() {
		this.classList.add('aras-scroller');
	}

	disconnectedCallback() {
		this._mutationObserver && this._mutationObserver.disconnect();
		this._intersectionObserver && this._intersectionObserver.disconnect();
		this._resizeObserver && this._resizeObserver.disconnect();
		this.removeEventListener('accordionToggle', this._accordionToggleHandler);
		// SteepGraph Modification
		// Developer    : Pratik Daud
		// Date         : 06/04/2026
		// Purpose      : Clean up maximize event listener to prevent memory leaks.
		// ==============================================
		// Changes Start
		this.removeEventListener('accordionMaximize', this._boundResizeAccordion);
		//Changes End
		this.removeEventListener('scroll', this._scrollHandler);
		window.removeEventListener('resize', this._resizeHandler);
	}

	async _mutationObserverHandler(mutations) {
		const relationshipIframes = await this._collectRelationshipIframes(mutations);
		const scrollables = await Promise.all(
			relationshipIframes.map((iframe) => this._loadScrollableElement(iframe))
		);
		this._connectResizeObserver(scrollables);
	}

		_loadScrollableElement(iframe) {
		return new Promise((resolve) => {
		         //Steepgraph Modification
                         //Changes Start
			 //Author : Pratik Daud
			const setupScrollableElement = (scrollableElement) => {
				const overflowY = this._isMaximized ? 'auto' : 'hidden';
				scrollableElement.style.overflowY = overflowY;
				this._store.set(
					iframe.closest('.aras-scroller__sticky') || iframe,
					scrollableElement
				);
			};

			if (iframe.contentWindow.grid) {
				const scrollableElement =
					iframe.contentWindow.grid._grid.view.scrollableElement;
				setupScrollableElement(scrollableElement);
				//Changes End
				resolve(scrollableElement);
				return;
			}

			const observer = new MutationObserver((mutations, observer) => {
				const grid = iframe.contentWindow.grid;
				const scrollableElement = grid && grid._grid.view.scrollableElement;
				if (scrollableElement) {
					setupScrollableElement(scrollableElement);
					resolve(scrollableElement);
					observer.disconnect();
				}
			});
			observer.observe(iframe.contentWindow.document, {
				childList: true,
				subtree: true
			});
		});
	}
        // SteepGraph Modification
	// Developer    : Pratik Daud
	// Date         : 06/04/2026
	// Purpose      : Validate mutation records to avoid runtime errors.
	// ==============================================
	// Changes Start
	async _collectRelationshipIframes(mutations) {
		const validMutations = (mutations || []).filter(
			(m) => m && m.type && m.target
		);
    //Changes End
		const iframes = validMutations.reduce((iframes, mutationRecord) => {
			if (mutationRecord.type !== 'attributes') {
				return iframes;
			}

			const switcherControl = mutationRecord.target;
			// SteepGraph Modification
			// Developer    : Pratik Daud
			// Date         : 06/04/2026
			// Purpose      : Ensure switcher control is valid before accessing attributes.
			// ==============================================
			// Changes Start
			if (!switcherControl || !switcherControl.getAttribute) {
				return iframes;
			}
            //Changes End
			const activePaneId = switcherControl.getAttribute('active-pane-id') || '';
			// SteepGraph Modification
			// Developer    : Pratik Daud
			// Date         : 06/04/2026
			// Purpose      : Clear cached scrollable reference when switching relationship tabs and delete stale store entry when accordion switches tab.
			// ==============================================
			// Changes Start
			const closestAccordion =
				switcherControl.closest && switcherControl.closest('.aras-accordion');

			//delete stale store entry when accordion switches tab
			this._store.delete(closestAccordion);
             //Changes End
			const relationshipIframe = document.getElementById(activePaneId);
			const isInfernoGridIFrame =
				relationshipIframe instanceof HTMLIFrameElement &&
				relationshipIframe.src.includes('relationshipsInfernoGrid.html');

			if (isInfernoGridIFrame) {
				iframes.push(relationshipIframe);
			}
			// SteepGraph Modification
			// Developer    : Pratik Daud
			// Date         : 06/04/2026
			// Purpose      : Adjust iframe height based on maximize state for non-grid views.
			// ==============================================
			// Changes Start 
			else if (relationshipIframe) {
				relationshipIframe.height = this._isMaximized
					? '100%'
					: `${relationshipInitialHeight}`;
			//Changes End
			}

			this._setupStickyWrapperNode(closestAccordion, isInfernoGridIFrame);

			return iframes;
		}, []);

		return await Promise.all(
			iframes.map((iframe) => {
				return new Promise((resolve) => {
					if (iframe.contentWindow.location.href === iframe.src) {
						resolve(iframe);
						return;
					}

					iframe.addEventListener('load', function loadHandler() {
						iframe.removeEventListener('load', loadHandler);
						resolve(iframe);
					});
				});
			})
		);
	}
        
	// SteepGraph Modification
	// Developer    : Pratik Daud
	// Date         : 06/04/2026
	// Purpose      : Expand iframe to full height when accordion is maximized.
	// ==============================================
	// Changes Start
	_adjustFrameSize(frame) {
		if (this._isMaximized) {
			frame.style.height = '100%';
			if (frame.contentWindow && frame.contentWindow.document) {
				frame.contentWindow.document.body.style.height = '100%';
			}
			return;
		}
    //Changes End
		const _window = frame.contentWindow;
		const gridContainer = _window.document.getElementById('gridTD');
		// SteepGraph Modification
		// Developer    : Pratik Daud
		// Date         : 06/04/2026
		// Purpose      : Prevent errors if grid container is not yet available.
		// ==============================================
		// Changes Start
		if (!gridContainer) return;
        //Changes End
		const gridHeader = gridContainer.querySelector('.aras-grid-header');
		if (!gridHeader) return;

		const topMargin = 12;
		const accordion = _window.frameElement.closest('aras-accordion');
		let accordionOffset = 0;
		if (accordion) {
			const accordionHead = accordion.firstElementChild;
			const accordionContent = accordion.lastElementChild;
			accordionOffset =
				accordionHead.offsetHeight +
				parseInt(window.getComputedStyle(accordionContent).paddingTop) * 2;
		}

		const scrollerHeight = _window.grid._grid.view.scroller.offsetHeight;
		const scrollableElement = _window.grid._grid.view.scrollableElement;
		const paginationOffset = _window.pagination
			? _window.pagination.offsetHeight +
			  parseInt(getComputedStyle(_window.pagination).marginTop)
			: 0;
		const gridOuterHeight = gridContainer.offsetTop + paginationOffset;
		const headerHeight = gridHeader.offsetHeight;
		const scrollbarWidth =
			scrollableElement.offsetHeight - scrollableElement.clientHeight;

		frame.style.height =
			Math.max(
				Math.min(
					headerHeight + gridOuterHeight + scrollerHeight + scrollbarWidth,
					this.clientHeight - accordionOffset - topMargin
				),
				frameMinHeight - accordionOffset
			) + 'px';
	}

	_adjustGridContainerSize(gridContainer) {
		if (gridContainer) {
			const gridFrameWindow = gridContainer.ownerDocument.defaultView;
			const paginationOffset = gridFrameWindow.pagination
				? gridFrameWindow.pagination.offsetHeight +
				  parseInt(getComputedStyle(gridFrameWindow.pagination).marginTop)
				: 0;
			const gridOuterHeight = gridContainer.offsetTop + paginationOffset;
			gridContainer.style.maxHeight = `calc(100% - ${gridOuterHeight}px)`;
		}
	}

	_resizeGridAndFrame(gridScroller) {
		if (gridScroller) {
			const gridFrameWindow = gridScroller.ownerDocument.defaultView;
			const gridContainer = gridScroller.closest('.aras-grid');
			const gridFrame = gridFrameWindow.frameElement;
			this._adjustGridContainerSize(gridContainer);
			this._adjustFrameSize(gridFrame);
		}
	}

	_handleResize(targets) {
		targets.forEach((target) => {
			const domElement = target.target;
			const targetOwnerFrame = domElement.ownerDocument.defaultView.frameElement;
			if (!targetOwnerFrame) return;
			const targetOwnerFrameStyle = window.getComputedStyle(targetOwnerFrame);
			if (targetOwnerFrameStyle.visibility === 'hidden') return;

			const [type, id] = [...domElement.dataset.scrollerMark.split('_')];
			switch (type) {
				case 'scroller':
					this._resizeGridAndFrame(domElement);
					this._calculateStickyHeight(domElement);
					break;
				case 'toolbar':
				case 'body':
					const gridScroller = domElement.ownerDocument.querySelector(
						'[data-scroller-mark="scroller_' + id + '"]'
					);
					this._resizeGridAndFrame(gridScroller);
					this._scrollerTop = this.getBoundingClientRect().top;
					this._calculateStickyHeight(gridScroller);
					break;
			}
		});
	}
        // SteepGraph Modification
	// Developer    : Pratik Daud
	// Date         : 06/04/2026
	// Purpose      : Attach ResizeObserver to grid elements for dynamic resizing.
	// ==============================================
	// Changes Start
	_connectResizeObserver(scrollableElements) {
	//Changes End
		scrollableElements.forEach((gridScroller, idx) => {
			const scroller = gridScroller.firstElementChild;
			const body = gridScroller.parentElement;
			const tollbars = gridScroller.ownerDocument.getElementById(
				'topContainer'
			);
			const gridWindow = gridScroller.ownerDocument.defaultView;
			if (
				!scroller ||
				!gridWindow ||
				!(scroller instanceof gridWindow.HTMLElement) ||
				!body ||
				!(body instanceof gridWindow.HTMLElement) ||
				!tollbars ||
				scroller.dataset.scrollerMark
			) {
				return;
			}

			gridWindow.frameElement.removeAttribute('height');
			const observer = new gridWindow.ResizeObserver(
				this._handleResize.bind(this)
			);

			scroller.dataset.scrollerMark = 'scroller_' + idx;
			body.dataset.scrollerMark = 'body_' + idx;
			tollbars.dataset.scrollerMark = 'toolbar_' + idx;

			observer.observe(scroller);
			observer.observe(body);
			observer.observe(tollbars);
		});
	}

	_setupStickyWrapperNode(accordion, isSticky) {
		if (!accordion) return;
		if (!accordion.parentNode) return;

		let accordionWrapperNode = accordion.closest('.aras-scroller__wrapper');

		if (!accordionWrapperNode) {
			accordionWrapperNode = document.createElement('div');
			accordionWrapperNode.classList.add('aras-scroller__wrapper');
			this._wrappers.add(accordionWrapperNode);

			//DO NOT touch iframe.src at all - this was causing blank content
			accordion.parentElement &&
				accordion.parentElement.replaceChild(accordionWrapperNode, accordion);
			accordionWrapperNode.appendChild(accordion);

			this._intersectionObserver &&
				this._intersectionObserver.observe(accordion);
			// SteepGraph Modification
			// Developer    : Pratik Daud
			// Date         : 06/04/2026
			// Purpose      : Ensure accordion has maximize attribute for UI control rendering.
			// ==============================================
			// Changes Start
			// Set maximized attribute so accordion renders the button
			if (!accordion.hasAttribute('maximized')) {
				accordion.setAttribute('maximized', 'false');
			}
			//Changes End
		}

		accordion.classList.toggle('aras-scroller__sticky', isSticky);
		// SteepGraph Modification
		// Developer    : Pratik Daud
		// Date         : 06/04/2026
		// Purpose      : Expand wrapper to full height when maximized.
		// ==============================================
		// Changes Start
		if (this._isMaximized) {
			accordionWrapperNode.style.height = '100%';
			return;
		}
        //Changes End
		if (!isSticky) {
			accordionWrapperNode.style.height = '';
		}
	}

	_calculateStickyHeight(gridScroller) {
		// SteepGraph Modification
		// Developer    : Pratik Daud
		// Date         : 06/04/2026
		// Purpose      : Skip height calculation when accordion is maximized.
		// ==============================================
		// Changes Start
		if (this._isMaximized) return;
		//Changes End
		const scrollableElement = gridScroller && gridScroller.parentElement;
		if (!scrollableElement) return;

		const frame = scrollableElement.ownerDocument.defaultView.frameElement;
		// SteepGraph Modification
		// Developer    : Pratik Daud
		// Date         : 06/04/2026
		// Purpose      : Ensure frame exists before calculating layout.
		// ==============================================
		// Changes Start
		if (!frame) return;
        //Changes End
		const sticky = frame.closest('.aras-accordion') || frame;
		// SteepGraph Modification
		// Developer    : Pratik Daud
		// Date         : 06/04/2026
		// Purpose      : Validate sticky container before applying height.
		// ==============================================
		// Changes Start
		if (!sticky) return;
        //Changes End
		if (sticky.classList.contains('aras-accordion') && sticky.collapsed) {
			// SteepGraph Modification
			// Developer    : Pratik Daud
			// Date         : 06/04/2026
			// Purpose      : Reset wrapper height when accordion is collapsed.
			// ==============================================
			// Changes Start
			if (sticky.parentElement) sticky.parentElement.style.height = '';
			//Changes End
			return;
		}

		const height =
			sticky.offsetHeight +
			scrollableElement.scrollHeight -
			scrollableElement.clientHeight;
            // SteepGraph Modification
			// Developer    : Pratik Daud
			// Date         : 06/04/2026
			// Purpose      : Dynamically adjust wrapper height based on grid scroll.
			// ==============================================
			// Changes Start
			if (sticky.parentElement) {
			//Changes End
				sticky.parentElement.style.height = `${height}px`;
			}
	}

	_accordionToggleHandler(event) {
		// SteepGraph Modification
		// Developer    : Pratik Daud
		// Date         : 06/04/2026
		// Purpose      : Ignore toggle events when accordion is maximized.
		// ==============================================
		// Changes Start
		if (this._isMaximized) return;
                //Changes End
		const target = event.target;
		// SteepGraph Modification
		// Developer    : Pratik Daud
		// Date         : 06/04/2026
		// Purpose      :  Ensure valid event target before processing.
		// ==============================================
		// Changes Start
		if (!target) return;
        //Changes End

		const iframe = target.querySelector(
			'.aras-switcher-pane_active iframe[src*="relationshipsInfernoGrid.html"]'
		);
		// SteepGraph Modification
		// Developer    : Pratik Daud
		// Date         : 06/04/2026
		// Purpose      :  Exit if no relationship iframe is found.
		// ==============================================
		// Changes Start
		if (!iframe) return;

		const gridScroller =
			iframe.contentWindow &&
			iframe.contentWindow.document.querySelector('.aras-grid-scroller');
		//Changes End
		this._calculateStickyHeight(gridScroller);
	}

	_intersectionObserverHandler(changes) {
		changes.forEach((change) => {
			const sticky = change.target;
			if (change.isIntersecting) {
				this._currentStickedElement = sticky;
				//Steepgraph Modification
				//Changes Start
				//Author : Pratik Daud
				return;
			}
			if (sticky.parentElement !== this.lastElementChild) {
				this._currentStickedElement =
					sticky.parentElement?.nextElementSibling?.firstElementChild;
				//Changes End
				return;
			}
			if (this._currentStickedElement === sticky) {
				this._currentStickedElement = null;
			}
		});
	}

	_scrollHandler() {
		if (this._currentStickedElement) {
			const wrapper = this._currentStickedElement.parentElement;
			const scrollableElement = this._store.get(this._currentStickedElement);

			if (!wrapper || !scrollableElement) {
				return;
			}
			scrollableElement.scrollTop =
				this._scrollerTop - wrapper.getBoundingClientRect().top;
		}
	}

	_resizeHandler = () => {
	    // SteepGraph Modification
		// Developer    : Pratik Daud
		// Date         : 06/04/2026
		// Purpose      : Skip resize logic when accordion is maximized.
		// ==============================================
		// Changes Start
		if (this._isMaximized) return;
        //Changes End
		const activeIframes = this.querySelectorAll(
			'.aras-switcher-pane_active iframe[src*="relationshipsInfernoGrid.html"]'
		);

		// SteepGraph Modification
		// Developer    : Pratik Daud
		// Date         : 06/04/2026
		// Purpose      : Recalculate iframe height on window resize.
		// ==============================================
		// Changes Start
		activeIframes.forEach((activeIframe) => this._adjustFrameSize(activeIframe));
		//Changes End
	};

	// SteepGraph Modification
	// Developer    : Pratik Daud
	// Date         : 06/04/2026
	// Purpose      : Handle accordion maximize/restore event and update layout accordingly.
	// ==============================================
	// Changes Start
	_resizeAccordion(event) {
		if (!(event instanceof CustomEvent)) return;
		if (!event.detail) return;

		const accordion = event.target;
		if (!accordion) return;

		const currentWrapper = accordion.closest('.aras-scroller__wrapper');
		if (!currentWrapper) return;

		this._isMaximized = event.detail.maximized === 'true';

		currentWrapper.classList.toggle(
			'aras-scroller__wrapper_maximized',
			this._isMaximized
		);

		// Hide all other wrappers
		this._wrappers.forEach((wrapper) => {
			const hidden = this._isMaximized && currentWrapper !== wrapper;
			wrapper.classList.toggle('aras-scroller__wrapper_hidden', hidden);
		});

		// Handle form vs relationship
		const itemForm = currentWrapper.querySelector('aras-form');
		if (itemForm) {
			this._resizeItemForm(itemForm);
		} else {
			const relationshipSwitcher = currentWrapper.querySelector('.aras-switcher');
			const activePaneId =
				relationshipSwitcher && relationshipSwitcher.getAttribute('active-pane-id');
			const relationshipIframe = document.getElementById(activePaneId || '');
			if (relationshipIframe) {
				this._resizeRelationship(relationshipIframe);
			}
		}

		if (this._isMaximized) {
			this._maximizeAccordion(currentWrapper);
			return;
		}

		this._restoreAccordion(currentWrapper);
	}
	
	// Purpose : Expand accordion and enable full scrollable area.
	_maximizeAccordion(wrapper) {
		const stickyElement = wrapper.firstElementChild;
		if (!stickyElement) return;

		const isCollapsed =
			stickyElement.classList.contains('aras-accordion') &&
			stickyElement.collapsed;
		if (isCollapsed) stickyElement.expand();

		wrapper.style.height = '100%';

		const scrollableElement = this._store.get(stickyElement);
		if (scrollableElement) {
			scrollableElement.style.overflowY = 'auto';
			if (scrollableElement.ownerDocument && scrollableElement.ownerDocument.body) {
				scrollableElement.ownerDocument.body.style.height = '100%';
			}
		}
	}
	
	// Purpose : Restore accordion to normal size and reset scroll behavior.
	_restoreAccordion(wrapper) {
		const stickyElement = wrapper.firstElementChild;
		if (!stickyElement) {
			wrapper.style.height = '';
			return;
		}

		const scrollableElement = this._store.get(stickyElement);
		if (scrollableElement) {
			scrollableElement.style.overflowY = 'hidden';
			const gridScroller = scrollableElement.firstElementChild;
			const gridFrameWindow = scrollableElement.ownerDocument.defaultView;
			const gridFrame = gridFrameWindow && gridFrameWindow.frameElement;
			if (gridFrame) this._adjustFrameSize(gridFrame);
			this._calculateStickyHeight(gridScroller);
			return;
		}

		wrapper.style.height = '';
	}
	
	// Purpose : Adjust item form height based on maximize state.
	_resizeItemForm(form) {
		const formHeight = window.formHeight;
		form.style.height = this._isMaximized
			? '100%'
			: formHeight ? `${formHeight}px` : '';
	}
	
	// Purpose : Adjust relationship iframe height depending on maximize state and type.
	_resizeRelationship(iframe) {
		if (!iframe) return;
		const isInfernoGridIFrame = iframe.src.includes('relationshipsInfernoGrid.html');
		if (isInfernoGridIFrame) {
			this._adjustFrameSize(iframe);
		} else {
			iframe.height = this._isMaximized ? '100%' : `${relationshipInitialHeight}`;
		}
	}
	//Changes End
}

